// ====================================================================================
// COMPONENT: SwingAnalysisSummary (Aesthetic: Dark Glass & High-Fidelity)
// Self-contained with SwingKPIBox, GlassContainer, charts, and all icons.
// ====================================================================================
import {
  AlertCircle,
  AlertTriangle,
  Target,
  Zap,
  Users,
  Brain as BrainIcon,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from "recharts";

// --- Helper: Glass Container ---
const GlassContainer = ({ title, subtitle, icon: Icon, headerActions, children, className = "" }) => (
  <div className={`bg-slate-900/40 border border-slate-700/50 rounded-xl relative flex flex-col overflow-hidden h-full ${className}`}>
    <div className="px-4 py-3 border-b border-slate-800/50 flex items-center justify-between bg-slate-900/20 shrink-0">
      <div className="flex items-center gap-2.5 overflow-hidden">
        {Icon && <Icon size={16} className="text-white flex-shrink-0" />}
        <div className="flex flex-col justify-center">
          <h3 className="text-xs font-bold text-white uppercase tracking-widest truncate shadow-black drop-shadow-sm leading-tight">
            {title}
          </h3>
          {subtitle && (
            <span className="text-[10px] text-slate-300 font-medium mt-0.5 truncate leading-normal pb-0.5">
              {subtitle}
            </span>
          )}
        </div>
      </div>
      {headerActions && <div className="flex-shrink-0 ml-2">{headerActions}</div>}
    </div>
    <div className="flex-grow overflow-y-auto custom-scrollbar p-0">
      {children}
    </div>
  </div>
);

// --- Helper: KPI Box ---
const SwingKPIBox = ({ title, value, unit = '', note = '', subtitle = '' }) => {
  let valueColor = 'text-white';
  let noteColor = 'text-slate-400';
  let borderColor = 'border-slate-700/50';
  let glowClass = '';
  let icon = null;

  if (title.includes('Winability')) {
    const numericValue = parseFloat(value);
    if (numericValue < 20) {
      valueColor = 'text-rose-400';
      noteColor = 'text-rose-300';
      borderColor = 'border-rose-500/20';
      glowClass = 'shadow-[0_0_15px_rgba(244,63,94,0.1)]';
      icon = <AlertCircle size={12} className="inline-block mr-1" />;
    } else if (numericValue < 40) {
      valueColor = 'text-amber-400';
      noteColor = 'text-amber-300';
      borderColor = 'border-amber-500/20';
      glowClass = 'shadow-[0_0_15px_rgba(251,191,36,0.1)]';
      icon = <AlertTriangle size={12} className="inline-block mr-1" />;
    } else {
      valueColor = 'text-emerald-400';
      noteColor = 'text-emerald-300';
      borderColor = 'border-emerald-500/20';
      glowClass = 'shadow-[0_0_15px_rgba(16,185,129,0.1)]';
    }
  }

  return (
    <div className={`bg-slate-900/40 p-4 rounded-xl border ${borderColor} ${glowClass} hover:bg-slate-800/40 transition-all duration-300 flex flex-col justify-between min-h-[100px] group`}>
      <div>
        <div className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1 group-hover:text-slate-400 transition-colors">{title}</div>
        <div className={`text-2xl font-bold ${valueColor} tracking-tight`}>
          {value}
          {unit && <span className="text-base text-slate-500 ml-1 font-medium">{unit}</span>}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-800/50">
        {subtitle && <div className="text-[10px] text-slate-400 font-medium truncate">{subtitle}</div>}
        {note && <div className={`text-[10px] ${noteColor} mt-0.5 flex items-center`}>{icon}{note}</div>}
      </div>
    </div>
  );
};

const getTierColor = (tierName) => {
  const key = tierName.toLowerCase();
  if (key.includes('gain')) return '#22c55e';
  if (key.includes('long_term')) return '#facc15';
  if (key.includes('defend') || key.includes('opponent')) return '#ef4444';
  return '#64748b';
};

// --- Main Component ---
const SwingAnalysisSummary = ({ swingData }) => {
  if (!swingData) return null;

  const { summary } = swingData;

  const winabilityValue = (summary.average_winability * 100).toFixed(1);
  let winabilityNote = "Critical Intervention";
  if (winabilityValue >= 40) winabilityNote = "Strong Position";
  else if (winabilityValue >= 20) winabilityNote = "Competitive";

  const conversionEfficiency = summary.campaign_total_supporters > 0
    ? ((summary.campaign_total_supporters / summary.campaign_total_swing_pool) * 100).toFixed(1)
    : '0.0';

  const persuadabilityRate = summary.campaign_total_voters > 0
    ? ((summary.campaign_total_persuadable / summary.campaign_total_voters) * 100).toFixed(1)
    : '0.0';

  const tierChartData = Object.entries(summary.strategic_tier_counts || {}).map(([name, count]) => ({
    name: name.replace(/_/g, ' ').replace('TARGET', ' TGT'),
    count: count,
    fill: getTierColor(name),
  }));

  const activationVectors = Object.entries(summary.dominant_activation_vectors || {}).map(([key, value]) => {
    const difficulty = key.includes('High') ? 'HIGH' : key.includes('Medium') ? 'MED' : 'LOW';
    const label = key.split(':')[1]?.trim() || key;
    return { difficulty, label, count: value };
  });

  return (
    <div className="space-y-6 flex flex-col">

      {/* Header with Metadata */}
      <div className="flex flex-wrap items-end justify-between gap-3 px-1">
        <div>
          <h2 className="text-lg font-bold text-white leading-none mb-2">Strategic Swing Analysis</h2>
          <div className="flex flex-wrap items-center gap-3">
            <div className="px-2 py-1 rounded bg-slate-900/50 border border-slate-700 text-[10px] text-slate-400 font-mono">
              v{swingData.metadata?.artemis_version || 'N/A'}
            </div>
            <div className="text-xs text-slate-400 font-medium">
              Targeting: <span className="text-indigo-400">{swingData.metadata?.target_party || 'N/A'}</span>
            </div>
            <div className="text-xs text-slate-500">
              • {summary.total_wards_analyzed} wards analyzed
            </div>
          </div>
        </div>

        {/* Win Probability Badge */}
        <div className="flex items-center gap-3 bg-slate-900/80 pl-4 pr-3 py-1.5 rounded-lg border border-slate-700/80 shadow-lg">
          <div className="text-right">
            <div className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Win Probability</div>
            <div className={`text-[10px] font-bold ${parseFloat(winabilityValue) >= 20 ? 'text-white' : 'text-rose-300'}`}>{winabilityNote}</div>
          </div>
          <div className={`text-2xl font-bold tabular-nums ${parseFloat(winabilityValue) >= 40 ? 'text-emerald-400' : parseFloat(winabilityValue) >= 20 ? 'text-amber-400' : 'text-rose-400'}`}>
            {winabilityValue}%
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SwingKPIBox
          title="Total Swing Pool"
          value={summary.campaign_total_swing_pool.toLocaleString()}
          subtitle="Total accessible voters"
        />
        <SwingKPIBox
          title="Identified Supporters"
          value={summary.campaign_total_supporters.toLocaleString()}
          subtitle={`${conversionEfficiency}% conversion efficiency`}
        />
        <SwingKPIBox
          title="Persuadable Voters"
          value={summary.campaign_total_persuadable.toLocaleString()}
          subtitle={`${persuadabilityRate}% of total electorate`}
        />
        <SwingKPIBox
          title="Turnout Potential"
          value={summary.campaign_total_turnout_potential.toLocaleString()}
          subtitle="Net gain from mobilization"
        />
      </div>

      {/* Two-Column Layout for Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Left: Tier Distribution */}
        <GlassContainer title="Strategic Tier Distribution" icon={Target}>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={tierChartData} layout="vertical" margin={{ right: 20, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334152" horizontal={false} />
              <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis
                type="category"
                dataKey="name"
                stroke="#94a3b8"
                fontSize={10}
                width={80}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                cursor={{ fill: '#33415240' }}
                contentStyle={{
                  backgroundColor: '#0f172a',
                  borderColor: '#334152',
                  borderRadius: '0.5rem',
                  fontSize: '11px',
                  color: '#fff',
                }}
                itemStyle={{ color: '#fff' }}
              />
              <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20}>
                {tierChartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} stroke="rgba(0,0,0,0.2)" />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </GlassContainer>

        {/* Right: Activation Difficulty Matrix */}
        <GlassContainer title="Voter Activation Matrix" icon={Zap}>
          <div className="space-y-3 h-[180px] overflow-y-auto custom-scrollbar pr-1">
            {activationVectors.map((vector, idx) => (
              <div key={idx} className="flex items-center justify-between group p-2 rounded-lg bg-slate-950/30 border border-slate-800/50 hover:border-slate-700 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`px-2 py-1 rounded text-[9px] font-bold uppercase tracking-wider flex-shrink-0 ${
                    vector.difficulty === 'HIGH' ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20' :
                    vector.difficulty === 'MED' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' :
                    'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                  }`}>
                    {vector.difficulty}
                  </div>
                  <span className="text-xs text-slate-300 group-hover:text-white transition-colors truncate">{vector.label}</span>
                </div>
                <div className="flex items-center gap-1.5 pl-2 flex-shrink-0">
                  <span className="text-sm font-bold text-white tabular-nums">{vector.count}</span>
                  <span className="text-[9px] text-slate-500 uppercase">Wards</span>
                </div>
              </div>
            ))}
          </div>
        </GlassContainer>
      </div>

      {/* Strategic Intelligence Panel (Command Console Style) */}
      <div className="bg-slate-950/50 p-5 rounded-xl border border-slate-800/80 relative overflow-hidden">
        {/* Decoration Line */}
        <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500/50"></div>

        <div className="flex items-start space-x-4 relative z-10">
          <div className="p-2 bg-slate-900 rounded-lg border border-slate-700 text-indigo-400 shadow-lg flex-shrink-0">
            <BrainIcon size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-xs font-bold text-white uppercase tracking-widest mb-4 flex items-center gap-2">
              Strategic Synthesis
              <span className="h-px flex-grow bg-slate-800 ml-2"></span>
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
              <div className="min-w-0">
                <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mb-1">Top Archetype</div>
                <div className="text-sm text-white font-bold truncate">
                  {Object.entries(summary.dominant_archetypes || {}).sort(([, a], [, b]) => b - a)[0]?.[0] || 'N/A'}
                </div>
                <div className="text-[10px] text-indigo-400 mt-0.5 font-mono">
                  {Object.entries(summary.dominant_archetypes || {}).sort(([, a], [, b]) => b - a)[0]?.[1].toLocaleString() || 0} voters
                </div>
              </div>

              <div className="min-w-0">
                <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mb-1">Primary Pathway</div>
                <div className="text-sm text-white font-bold truncate">
                  {Object.keys(summary.dominant_persuasion_pathways || {})[0] || 'N/A'}
                </div>
                <div className="text-[10px] text-emerald-400 mt-0.5 font-mono">
                  {Object.entries(summary.dominant_persuasion_pathways || {})[0]?.[1] || 0} wards optimized
                </div>
              </div>

              <div className="min-w-0">
                <div className="text-[9px] text-slate-500 font-bold uppercase tracking-wider mb-1">Resource Priority</div>
                <div className="text-sm text-white font-bold truncate">
                  {Object.keys(summary.resource_allocation_counts || {})[0] || 'N/A'} Allocation
                </div>
                <div className="text-[10px] text-amber-400 mt-0.5 font-mono">
                  {Object.entries(summary.resource_allocation_counts || {})[0]?.[1] || 0} wards
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Archetype Distribution List (Compact Glass) */}
      <GlassContainer title="Dominant Voter Archetypes" icon={Users}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-3">
          {Object.entries(summary.dominant_archetypes || {})
            .sort(([, a], [, b]) => b - a)
            .map(([name, count]) => (
              <div key={name} className="flex items-center justify-between bg-slate-950/30 border border-slate-800/50 px-3 py-2 rounded-lg hover:border-slate-600 transition-colors group">
                <span className="text-[10px] font-bold text-slate-400 group-hover:text-slate-200 transition-colors truncate mr-2">{name}</span>
                <span className="text-[10px] font-mono font-bold text-indigo-300 bg-indigo-500/10 px-2 py-0.5 rounded border border-indigo-500/20 flex-shrink-0">
                  {count.toLocaleString()}
                </span>
              </div>
            ))}
        </div>
      </GlassContainer>
    </div>
  );
};

export default SwingAnalysisSummary;
